import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';
import { BRAND_LOGO_CONTENT_TYPES } from '../../services/brandsService';
import {
  BRAND_NAME_MAX_LENGTH,
  brandToForm,
  hasBrandFormErrors,
  sanitizePhoneInput,
  toColorInputValue,
  validateBrandForm,
} from '../../Utils/brandForm';
import { colors, typography } from '../../styles/theme';
import {
  brandsErrorTextSx,
  brandsFieldLabelSx,
  brandsFieldSx,
  brandsHelperTextSx,
  brandsLogoBarSx,
  brandsLogoImageSx,
  brandsLogoPreviewSx,
  brandsSecondaryButtonSx,
  brandsSectionLabelSx,
  brandsSwatchInputStyle,
  brandsTextColors,
} from './BrandsModal.styles';

const ROW_SX = {
  display: 'flex',
  gap: 1.75,
  alignItems: 'flex-start',
  flexDirection: { xs: 'column', sm: 'row' },
  '& > *': { flex: 1, minWidth: 0, width: '100%' },
};

const LOGO_FORMAT_ERROR = 'Formato no permitido. Usá PNG, JPEG o WebP.';

/**
 * Alta y edición de una marca.
 *
 * El logo no viaja en `/upsertbrand`: la key de S3 incluye el id de la marca,
 * así que subirlo exige que ya exista. Para que el alta no se parta en dos
 * pasos visibles, en una marca nueva el archivo se guarda acá y sube solo
 * después del create; en una marca que ya existe se sube en el momento.
 *
 * @param {{
 *   formId?: string,
 *   brand: object|null,
 *   saving: boolean,
 *   uploadingLogo: boolean,
 *   error?: string,
 *   onSave: (form: object, options: {
 *     makeDefault: boolean,
 *     logoFile: File|null,
 *   }) => void,
 *   onUploadLogo: (file: File) => void,
 *   onRemoveLogo: () => void,
 * }} props
 */
export default function BrandEditor({
  formId = 'brand-editor-form',
  brand,
  saving,
  uploadingLogo,
  error = '',
  onSave,
  onUploadLogo,
  onRemoveLogo,
}) {
  const fileInputRef = useRef(null);
  const brandId = brand?.id || '';
  const isEditing = Boolean(brandId);

  const [form, setForm] = useState(() => brandToForm(brand));
  const [errors, setErrors] = useState({});
  const [makeDefault, setMakeDefault] = useState(false);
  const [pendingLogo, setPendingLogo] = useState(null);
  const [pendingLogoUrl, setPendingLogoUrl] = useState('');
  const [logoError, setLogoError] = useState('');

  useEffect(() => {
    setForm(brandToForm(brand));
    setErrors({});
    setMakeDefault(false);
    setLogoError('');
    setPendingLogo(null);
    setPendingLogoUrl('');
  }, [brand]);

  // El preview del archivo pendiente es un blob local: hay que liberarlo.
  useEffect(() => () => {
    if (pendingLogoUrl) URL.revokeObjectURL(pendingLogoUrl);
  }, [pendingLogoUrl]);

  const busy = saving || uploadingLogo;
  const alreadyDefault = brand?.is_default === true;
  const previewUrl = pendingLogoUrl || form.logo_url;

  const handleChange = (field, value) => {
    const next = (field === 'phone' || field === 'whatsapp')
      ? sanitizePhoneInput(value)
      : value;
    setForm((prev) => ({ ...prev, [field]: next }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  // Avisar al salir del campo y no recién al guardar: un teléfono corto o un
  // mail sin arroba se corrige mientras el usuario todavía está ahí.
  const handleBlur = (field) => {
    const fieldErrors = validateBrandForm(form);
    setErrors((prev) => ({ ...prev, [field]: fieldErrors[field] || '' }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const nextErrors = validateBrandForm(form);
    setErrors(nextErrors);
    if (hasBrandFormErrors(nextErrors)) return;
    onSave(form, {
      makeDefault,
      logoFile: pendingLogo,
    });
  };

  const clearPendingLogo = () => {
    setPendingLogo(null);
    setPendingLogoUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return '';
    });
  };

  const handleLogoChange = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    if (!BRAND_LOGO_CONTENT_TYPES.includes(file.type)) {
      setLogoError(LOGO_FORMAT_ERROR);
      return;
    }
    setLogoError('');

    if (isEditing) {
      onUploadLogo(file);
      return;
    }

    setPendingLogo(file);
    setPendingLogoUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
  };

  const handleRemoveLogo = () => {
    setLogoError('');
    if (pendingLogo) {
      clearPendingLogo();
      return;
    }
    onRemoveLogo();
  };

  const renderTextField = (field, label, options = {}) => (
    <Box>
      <Typography component="label" htmlFor={`${formId}-${field}`} sx={brandsFieldLabelSx}>
        {label}
        {options.required ? ' *' : ''}
      </Typography>
      <TextField
        id={`${formId}-${field}`}
        size="small"
        fullWidth
        value={form[field]}
        onChange={(event) => handleChange(field, event.target.value)}
        onBlur={() => handleBlur(field)}
        disabled={busy}
        error={Boolean(errors[field])}
        helperText={errors[field] || ''}
        placeholder={options.placeholder}
        type={options.type || 'text'}
        inputProps={options.maxLength ? { maxLength: options.maxLength } : undefined}
        sx={brandsFieldSx}
      />
    </Box>
  );

  const renderColorField = (field, label) => (
    <Box>
      <Typography component="label" htmlFor={`${formId}-${field}`} sx={brandsFieldLabelSx}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <input
          type="color"
          aria-label={`${label} (selector)`}
          value={toColorInputValue(form[field])}
          onChange={(event) => handleChange(field, event.target.value.toUpperCase())}
          disabled={busy}
          style={{ ...brandsSwatchInputStyle, cursor: busy ? 'default' : 'pointer' }}
        />
        <TextField
          id={`${formId}-${field}`}
          size="small"
          fullWidth
          value={form[field]}
          onChange={(event) => handleChange(field, event.target.value)}
          onBlur={() => handleBlur(field)}
          disabled={busy}
          error={Boolean(errors[field])}
          placeholder="#1F4B4A"
          sx={{
            ...brandsFieldSx,
            '& .MuiOutlinedInput-input': { fontFamily: typography.fontFamily.mono, fontSize: '13px' },
          }}
        />
      </Box>
      {errors[field] ? (
        <Typography sx={{ ...brandsErrorTextSx, fontSize: '12px', mt: 0.5 }}>{errors[field]}</Typography>
      ) : null}
    </Box>
  );

  return (
    <Box
      component="form"
      id={formId}
      noValidate
      onSubmit={handleSubmit}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2.25,
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        <Typography sx={brandsSectionLabelSx}>Identidad</Typography>
        <Box sx={ROW_SX}>
          {renderTextField('brand_name', 'Nombre', {
            required: true,
            maxLength: BRAND_NAME_MAX_LENGTH,
            placeholder: 'Ej. Terrio Prop',
          })}
          {renderTextField('email', 'Email de contacto', {
            type: 'email',
            placeholder: 'contacto@ejemplo.com',
          })}
        </Box>

        <Box>
          <Typography sx={brandsFieldLabelSx}>Logo</Typography>
          <Box sx={brandsLogoBarSx}>
            <Box sx={brandsLogoPreviewSx}>
              {previewUrl ? (
                <Box component="img" src={previewUrl} alt="Logo de la identidad" sx={brandsLogoImageSx} />
              ) : (
                <Typography sx={{ fontSize: '11px', color: brandsTextColors.muted }}>Sin logo</Typography>
              )}
            </Box>

            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1.5,
                flexWrap: 'wrap',
              }}
            >
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={busy}
                  startIcon={uploadingLogo ? <CircularProgress size={14} thickness={5} sx={{ color: 'inherit' }} /> : null}
                  sx={brandsSecondaryButtonSx}
                >
                  {previewUrl ? 'Cambiar logo' : 'Subir logo'}
                </Button>
                {previewUrl ? (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleRemoveLogo}
                    disabled={busy}
                    sx={{
                      ...brandsSecondaryButtonSx,
                      color: brandsTextColors.danger,
                      borderColor: 'rgba(238,87,87,0.35)',
                    }}
                  >
                    Quitar
                  </Button>
                ) : null}
              </Box>
              <Typography sx={{ ...brandsHelperTextSx, fontSize: '11px', whiteSpace: 'nowrap' }}>
                PNG, JPEG o WebP
              </Typography>
            </Box>

            <input
              ref={fileInputRef}
              type="file"
              accept={BRAND_LOGO_CONTENT_TYPES.join(',')}
              onChange={handleLogoChange}
              style={{ display: 'none' }}
            />
          </Box>
          {logoError ? (
            <Typography sx={{ ...brandsErrorTextSx, fontSize: '12px', mt: 0.5 }}>{logoError}</Typography>
          ) : null}
          {pendingLogo ? (
            <Typography sx={{ ...brandsHelperTextSx, mt: 0.5 }}>
              Se sube cuando guardes la identidad.
            </Typography>
          ) : null}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        <Typography sx={brandsSectionLabelSx}>Colores</Typography>
        <Box sx={ROW_SX}>
          {renderColorField('primary_color', 'Color primario')}
          {renderColorField('secondary_color', 'Color secundario')}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        <Typography sx={brandsSectionLabelSx}>Contacto</Typography>
        <Box sx={ROW_SX}>
          {renderTextField('phone', 'Teléfono', { type: 'tel', placeholder: '+54 9 11 5555-5555' })}
          {renderTextField('whatsapp', 'WhatsApp', { type: 'tel', placeholder: '+5491155555555' })}
        </Box>
      </Box>

      {alreadyDefault ? (
        <Typography sx={brandsHelperTextSx}>
          Es tu identidad principal: la usan los enlaces compartidos que no fijaron
          una identidad concreta. Para cambiarla, marcá otra como principal.
        </Typography>
      ) : (
        <FormControlLabel
          control={(
            <Checkbox
              checked={makeDefault}
              onChange={(event) => setMakeDefault(event.target.checked)}
              disabled={busy}
              size="small"
              sx={{ color: colors.border, '&.Mui-checked': { color: colors.primary } }}
            />
          )}
          label={(
            <Typography sx={{ color: brandsTextColors.secondary, fontSize: '13px' }}>
              Usar como identidad principal
            </Typography>
          )}
          sx={{ ml: 0 }}
        />
      )}

      {error ? <Typography sx={brandsErrorTextSx}>{error}</Typography> : null}
    </Box>
  );
}
